import React, { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";

export default function AboutUs() {
  const [isConsultOpen, setIsConsultOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Close bottom CTA dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsConsultOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const featureCards = [
    {
      title: "Virtual Consultations",
      desc: "Speak with licensed doctors and partner pharmacists by chat, voice or video, wherever you are.",
      icon: (
        <svg className="w-8 h-8" viewBox="0 0 24 24" fill="none" aria-hidden>
          <path d="M12 2l3 6 6 .5-4.5 3.9 1.5 6L12 16l-6 2.4 1.5-6L3 8.5 9 8 12 2z" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      ),
    },
    {
      title: "Licensed Doctors & Pharmacists",
      desc: "Our certified healthcare professionals guide you from diagnosis to the right medicine.",
      icon: (
        <svg className="w-8 h-8" viewBox="0 0 24 24" fill="none" aria-hidden>
          <path d="M12 2v20M2 12h20" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      ),
    },
    {
      title: "Verified Medicines, Fast Delivery",
      desc: "Genuine, NAFDAC-approved medicines with same-day and next-day delivery.",
      icon: (
        <svg className="w-8 h-8" viewBox="0 0 24 24" fill="none" aria-hidden>
          <path d="M3 12h14l4 5V7l-4 5H3z" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      ),
    },
  ];

  const medicalTeam = [
    { name: "Dr. Precious Ohakosim", role: "CEO and Chief Medical Online Consultant", img: "/ify.jpg" },
    { name: "Dr. Glory Ekanem", role: "Medical Online Consultant", img: "/glory.jpg" },
    { name: "Dr. Gift Seikakere", role: "Medical Online Consultant", img: "/gift.jpg" },
  ];

  const techOpsTeam = [
    { name: "Charles Ademola", role: "CTO", img: "/char2.jpg" },
    { name: "Oselumehse Beckley", role: "Healthcare Assistant", img: "/ose.jpg" },
    { name: "Izunna Fortune", role: "Customer Support", img: "/izunna.jpg" },
  ];

  return (
    <div className="min-h-screen bg-light text-gray-900">
      <header className="bg-gradient-to-r from-primary to-primary/80 text-white">
        <Navbar />
        <div className="max-w-7xl mx-auto px-6 py-20">
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
            <h1 className="text-3xl md:text-5xl font-extrabold leading-tight">
              About <span className="text-white/95">ProseMediStore</span>
            </h1>
            <p className="mt-4 max-w-2xl text-blue-50 text-lg md:text-xl">
              ProseMediStore is an online pharmacy and telemedicine service. We connect you with licensed doctors and partner pharmacists for virtual consultations, and deliver genuine medicines to your door. From your first consultation to your follow-up, we cover the entire care journey.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <a href="#mission" className="inline-flex items-center justify-center px-6 py-3 bg-white text-primary font-semibold rounded-lg shadow hover:opacity-95 transition">
                Our Mission
              </a>
              <a href="#contact" className="inline-flex items-center justify-center px-6 py-3 border border-white/30 text-white rounded-lg hover:bg-white/10 transition">
                Contact Us
              </a>
            </div>
          </motion.div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 -mt-12 relative z-10">
        {/* Mission */}
        <section id="mission" className="bg-white rounded-2xl shadow p-8 md:p-12">
          <motion.div initial={{ opacity: 0, y: 8 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5 }}>
            <h2 className="text-2xl font-bold text-gray-800">Our Mission</h2>
            <p className="mt-3 text-gray-600 max-w-3xl">
              To combine technology, clinical expertise and a reliable supply chain so that anyone in Nigeria can speak with a doctor or pharmacist, understand their health and get genuine medicines without stress. We make consultation, prescription, delivery and referral seamless and secure.
            </p>
          </motion.div>
        </section>

        {/* Feature Cards */}
        <section className="mt-8 grid gap-6 grid-cols-1 md:grid-cols-3">
          {featureCards.map((f, i) => (
            <motion.div
              key={f.title}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.12 }}
              className="bg-white rounded-xl p-6 shadow hover:shadow-lg transition flex gap-4 items-start"
            >
              <div className="bg-primary/10 text-primary p-3 rounded-lg shrink-0">
                {f.icon}
              </div>
              <div>
                <h3 className="font-semibold text-gray-800">{f.title}</h3>
                <p className="mt-1 text-sm text-gray-500">{f.desc}</p>
              </div>
            </motion.div>
          ))}
        </section>

        {/* Stats + Story */}
        <section className="mt-10 grid gap-6 grid-cols-1 lg:grid-cols-3">
          <div className="lg:col-span-2 bg-white p-8 rounded-2xl shadow">
            <motion.h3 initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} className="text-xl font-semibold text-gray-800">How we started</motion.h3>
            <motion.p initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} className="mt-3 text-gray-600">
              Founded by a team of healthcare professionals and technologists, ProseMediStore was built on a simple idea: everyone should be able to get trusted medical advice and genuine medicines without stress. We offer virtual consultations with licensed doctors and partner pharmacists, patient education, referrals for in-person care and a reliable online pharmacy, all in one place. So every customer can get the right advice first, then the right medicine.
            </motion.p>
            
            <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-4">
              <div className="bg-primary/5 rounded-lg p-4 text-center flex flex-col justify-center">
                <p className="text-3xl font-bold text-primary">20k+</p>
                <p className="text-xs font-medium text-gray-600 mt-1 uppercase tracking-wider">Orders delivered</p>
              </div>
              <div className="bg-primary/5 rounded-lg p-4 text-center flex flex-col justify-center">
                <p className="text-3xl font-bold text-primary">4.9</p>
                <p className="text-xs font-medium text-gray-600 mt-1 uppercase tracking-wider">Avg. rating</p>
              </div>
              <div className="bg-primary/5 rounded-lg p-4 text-center flex flex-col justify-center">
                <p className="text-3xl font-bold text-primary">50+</p>
                <p className="text-xs font-medium text-gray-600 mt-1 uppercase tracking-wider">Partner pharmacists</p>
              </div>
              <div className="bg-primary/5 rounded-lg p-4 text-center flex flex-col items-center justify-center">
                <svg className="w-8 h-8 text-primary mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-3-3v6m-9 2.428v-4.856c0-.528.281-1.01.737-1.24L11.263 3.12a1.5 1.5 0 011.474 0l8.526 4.204c.456.23.737.712.737 1.24v4.856c0 5.093-3.6 9.61-8.526 10.96a1.5 1.5 0 01-.848 0C3.6 18.038 0 13.521 0 8.428z" />
                </svg>
                <p className="text-xs font-bold text-primary uppercase tracking-wider leading-tight">MDCN-Licensed<br/>Doctors</p>
                <p className="text-[10px] text-gray-500 mt-1 leading-tight">Verified medical<br/>professionals</p>
              </div>
            </div>
          </div>

          <aside className="bg-white p-6 rounded-2xl shadow flex flex-col justify-between">
            <div>
              <h4 className="text-lg font-semibold text-gray-800">Quality & Safety</h4>
              <p className="mt-2 text-sm text-gray-500">
                We run supplier audits, batch verification and safe-packaging checks for every order, and every consultation follows clinical guidelines and patient privacy standards.
              </p>
            </div>
            <div className="mt-6">
              <h5 className="text-sm text-gray-500">Headquarters</h5>
              <p className="text-sm text-gray-700 mt-1">Administrative Office: 3C7J+8PW. Utako</p>
            </div>
          </aside>
        </section>

        {/* Refactored Team Section */}
        <section className="mt-10 bg-white p-8 md:p-12 rounded-2xl shadow">
          <div className="max-w-2xl mx-auto text-center sm:text-left sm:mx-0 mb-10">
            <motion.h3 
              initial={{ opacity: 0, y: 10 }} 
              whileInView={{ opacity: 1, y: 0 }} 
              viewport={{ once: true }}
              className="text-3xl font-bold tracking-tight text-primary sm:text-4xl"
            >
              Meet Our Team
            </motion.h3>
            
            <motion.p 
              initial={{ opacity: 0, y: 10 }} 
              whileInView={{ opacity: 1, y: 0 }} 
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="mt-4 text-base leading-relaxed text-slate-600 sm:text-lg"
            >
              We are a dedicated team of healthcare professionals and technologists, united by one mission: making quality care easy to reach for every Nigerian.
            </motion.p>
          </div>
          
          <div className="space-y-14">
            
            {/* 1. Our Medical Team */}
            <motion.div initial={{ opacity: 0, y: 10 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
              <div className="border-b border-gray-100 pb-3 mb-6">
                <h4 className="text-xl font-bold text-slate-800">1. Our Medical Team</h4>
              </div>
              <p className="text-slate-600 mb-8 max-w-3xl">
                Licensed doctors who lead our virtual consultations, provide patient education and refer for in-person care when needed.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
                {medicalTeam.map((m, idx) => (
                  <article key={m.name} className="bg-slate-50/50 border border-slate-100 rounded-xl p-6 text-center hover:shadow-md transition-shadow">
                    <img 
                      src={m.img} 
                      alt={m.name} 
                      className="mx-auto w-28 h-28 rounded-full object-cover border-4 border-white shadow-sm bg-slate-200" 
                    />
                    <h5 className="mt-5 font-bold text-gray-900">{m.name}</h5>
                    <p className="text-sm font-semibold text-emerald-600 mt-1">{m.role}</p>
                  </article>
                ))}
              </div>
            </motion.div>

            {/* 2. Our Partner Pharmacists */}
            <motion.div initial={{ opacity: 0, y: 10 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
              <div className="border-b border-gray-100 pb-3 mb-6">
                <h4 className="text-xl font-bold text-slate-800">2. Our Partner Pharmacists</h4>
              </div>
              <div className="bg-slate-50 border border-slate-100 rounded-xl p-6 md:p-8">
                <p className="text-slate-600 leading-relaxed max-w-4xl">
                  We work with a trusted network of licensed partner pharmacists across Nigeria who offer medicine consultations, dispense and verify every order, and answer your medicine questions.
                </p>
              </div>
            </motion.div>

            {/* 3. Technology & Operations */}
            <motion.div initial={{ opacity: 0, y: 10 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
              <div className="border-b border-gray-100 pb-3 mb-6">
                <h4 className="text-xl font-bold text-slate-800">3. Technology & Operations</h4>
              </div>
              <p className="text-slate-600 mb-8 max-w-3xl">
                The people who keep the platform running and make sure you are looked after from start to finish.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
                {techOpsTeam.map((m, idx) => (
                  <article key={m.name} className="bg-slate-50/50 border border-slate-100 rounded-xl p-6 text-center hover:shadow-md transition-shadow">
                    <img 
                      src={m.img} 
                      alt={m.name} 
                      className="mx-auto w-28 h-28 rounded-full object-cover border-4 border-white shadow-sm bg-slate-200" 
                    />
                    <h5 className="mt-5 font-bold text-gray-900">{m.name}</h5>
                    <p className="text-sm font-semibold text-emerald-600 mt-1">{m.role}</p>
                  </article>
                ))}
              </div>
            </motion.div>

          </div>
        </section>
        
        {/* CTA */}
        <section id="contact" className="mt-10 mb-20 bg-gradient-to-r from-primary to-primary/80 text-white rounded-2xl p-8 md:p-12 text-center shadow-lg relative z-20">
          <motion.h3 initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} className="text-2xl md:text-3xl font-semibold">
            Ready to get started?
          </motion.h3>
          <p className="mt-3 text-blue-50 max-w-xl mx-auto text-lg">
            Speak with a doctor or pharmacist, get the right medicine and have it delivered safely.
          </p>
          
          <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-4">
            
            {/* Dropdown Button: Consult an Expert */}
            <div className="relative inline-block text-left w-full sm:w-auto" ref={dropdownRef}>
              <button
                onClick={() => setIsConsultOpen(!isConsultOpen)}
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-3.5 bg-blue-600 text-white rounded-xl font-semibold shadow hover:bg-blue-700 transition-all"
              >
                Consult an Expert
                <svg
                  className={`w-4 h-4 transition-transform duration-200 ${
                    isConsultOpen ? "rotate-180" : ""
                  }`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              <AnimatePresence>
                {isConsultOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 8 }}
                    transition={{ duration: 0.2, ease: "easeOut" }}
                    className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 w-[calc(100vw-3rem)] sm:w-80 bg-white border border-gray-100 rounded-2xl shadow-2xl z-50 overflow-hidden text-left"
                  >
                    <div className="p-2 space-y-1">
                      <a
                        href="https://wa.me/2348162738527?text=Hello,%20I%20would%20like%20to%20speak%20with%20a%20doctor."
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block p-3 hover:bg-slate-50 rounded-xl transition-colors group"
                      >
                        <span className="block font-semibold text-gray-900 group-hover:text-primary transition-colors text-sm">
                          Speak with a Doctor
                        </span>
                        <span className="block text-xs text-gray-500 mt-1 leading-snug">
                          Virtual consultation with a licensed medical doctor for assessment of your symptoms, diagnosis, treatment advice, prescriptions and referral for in-person care when needed.
                        </span>
                      </a>
                      <a
                        href="https://wa.me/2348162738527?text=Hello,%20I%20would%20like%20to%20speak%20with%20a%20pharmacist."
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block p-3 hover:bg-slate-50 rounded-xl transition-colors group"
                      >
                        <span className="block font-semibold text-gray-900 group-hover:text-primary transition-colors text-sm">
                          Speak with a Pharmacist
                        </span>
                        <span className="block text-xs text-gray-500 mt-1 leading-snug">
                          Professional advice from our partner pharmacists on your medicines, including correct use, dosage, side effects, drug interactions and over-the-counter options.
                        </span>
                      </a>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Secondary Button: Shop Medicines */}
            <a
              href="/#shop"
              className="w-full sm:w-auto inline-flex items-center justify-center px-6 py-3.5 bg-white text-primary rounded-xl font-semibold hover:bg-blue-50 transition-all shadow-sm"
            >
              Shop Medicines
            </a>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}